import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { CalendarDateFormatter, CalendarView } from 'angular-calendar';
import { Bloqueio } from './calendar.interface';
import { CalendarFormatter } from './calendar-formatter';
import { debounceTime, fromEvent, Subscription } from 'rxjs';
import { DateUtilsService } from 'src/app/modules/shared/services/date-utils/date-utils.service';

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  providers: [
    {
      provide: CalendarDateFormatter,
      useClass: CalendarFormatter,
    },
  ],
})
export class CalendarComponent {
  @ViewChild('timeSelector') timeSelector!: TemplateRef<any>;
  @Input() agenda: any = null;
  @Input() selectedDateTime: string | null = null;
  @Input() selectedRenavam: string | null = null;
  @Input() selectedConsorcio: string | null = null;
  @Output() selectedDate = new EventEmitter<string | null>();
  private overlayRef: OverlayRef | null = null;
  CalendarView = CalendarView;
  view: CalendarView = CalendarView.Month;
  viewDate: Date = new Date();

  selectedDay: Date | null = null;
  selectedTime: string | null = null;
  confirmed = false;
  availableTimes: string[] = [];

  confirmedAppointment: {
    date: Date;
    time: string;
  } | null = null;

  fullyBlockedDays = new Set<string>();
  partiallyBlockedTimes = new Map<string, { start: string; end: string }[]>();

  resizeSubscription!: Subscription;

  constructor(
    private overlay: Overlay,
    private vcr: ViewContainerRef,
    private cdr: ChangeDetectorRef,
    private dateUtilsService: DateUtilsService,
  ) {}

  ngOnInit() {
    this.resizeSubscription = fromEvent(window, 'resize')
      .pipe(debounceTime(300))
      .subscribe(() => {
        this.viewDate = new Date(this.viewDate);
        this.cdr.detectChanges();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['agenda']) {
      if (this.agenda) {
        this.setBlockedDates(this.agenda.indisponibilidades);
      } else {
        this.partiallyBlockedTimes.clear();
        this.fullyBlockedDays.clear();
      }

      if (
        changes['agenda'].currentValue?.id !==
          changes['agenda'].previousValue?.id &&
        changes['agenda'].previousValue
      ) {
        this.confirmedAppointment = null;
        this.confirmed = false;
        this.selectedDate.emit(null);
      }
    }

    if (changes['selectedDateTime']) {
      const { date, time } = this.splitDateTime(this.selectedDateTime!);

      if (date && time) {
        this.confirmedAppointment = {
          date,
          time,
        };
      }

      this.confirmed = !!this.selectedDateTime;
    }
  }

  private splitDateTime(isoString: string) {
    if (!isoString) {
      return { date: null, time: null };
    }

    if (!isoString.endsWith('Z')) {
      isoString = isoString + '.000Z';
    }

    const date = new Date(isoString);
    const time = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    return { date, time };
  }

  setBlockedDates(indisponibilidades: Bloqueio[]) {
    this.partiallyBlockedTimes.clear();
    this.fullyBlockedDays.clear();

    for (const indisponibilidade of indisponibilidades) {
      const localDate = this.parseLocalDate(indisponibilidade.diaBloqueio);
      const dateStr = localDate.toDateString();

      if (indisponibilidade.todosHorarios) {
        this.fullyBlockedDays.add(dateStr);
      } else if (indisponibilidade.horarios?.length) {
        const blockedRanges = indisponibilidade.horarios.map((h) => {
          return {
            start: h.inicio.slice(0, 5),
            end: h.fim.slice(0, 5),
          };
        });

        const existingRanges = this.partiallyBlockedTimes.get(dateStr) || [];
        this.partiallyBlockedTimes.set(dateStr, [
          ...existingRanges,
          ...blockedRanges,
        ]);
      }
    }
  }

  setView(view: CalendarView) {
    this.view = view;
  }

  isBlocked(date: Date): boolean {
    return this.fullyBlockedDays.has(date.toDateString());
  }

  dayClicked(event: { day: any; sourceEvent: MouseEvent | KeyboardEvent }) {
    if (!(event.sourceEvent instanceof MouseEvent)) return;
    if (this.isBlocked(event.day.date)) return;

    this.selectedDay = event.day.date;
    this.selectedTime = null;
    this.confirmed = false;

    this.fetchAvailableTimes(event.day.date);
    this.openTimeSelector(event.sourceEvent);
  }

  fetchAvailableTimes(date: Date) {
    if (!this.agenda) {
      this.availableTimes = [];
      return;
    }

    if (this.dateUtilsService.isDayPassed(date)) {
      this.availableTimes = [];
      return;
    }

    const weekdayMap: Record<number, string> = {
      0: 'DOMINGO',
      1: 'SEGUNDA',
      2: 'TERCA',
      3: 'QUARTA',
      4: 'QUINTA',
      5: 'SEXTA',
      6: 'SABADO',
    };

    const dayName = weekdayMap[date.getDay()];
    const disponibilidade = this.agenda.disponibilidades.find(
      (d: any) => d.dia === dayName,
    );

    if (!disponibilidade) {
      this.availableTimes = [];
      return;
    }

    let availableTimes = disponibilidade.horarios.map((horario: any) => {
      const [hh, mm] = horario.inicio.split(':');
      return `${hh}:${mm}`;
    });

    const dateStr = date.toDateString();

    if (this.partiallyBlockedTimes.has(dateStr)) {
      const blockedRanges = this.partiallyBlockedTimes.get(dateStr)!;

      availableTimes = availableTimes.filter((time: any) => {
        return !blockedRanges.some((range) =>
          this.isTimeInRange(time, range.start, range.end),
        );
      });
    }

    if (this.dateUtilsService.isSameDay(date)) {
      availableTimes = availableTimes.filter(
        (time: any) => !this.dateUtilsService.isTimePassed(time),
      );
    }

    return (this.availableTimes = availableTimes);
  }

  private isTimeInRange(time: string, start: string, end: string): boolean {
    return time >= start && time < end;
  }

  openTimeSelector(domEvent: MouseEvent) {
    this.closeOverlay();

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(domEvent.target as HTMLElement)
      .withPositions([
        {
          originX: 'center',
          originY: 'center',
          overlayX: 'center',
          overlayY: 'center',
        },
      ]);

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'transparent-backdrop',
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });

    this.overlayRef.backdropClick().subscribe(() => this.closeOverlay());
    this.overlayRef.attach(new TemplatePortal(this.timeSelector, this.vcr));
  }

  closeOverlay() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  selectTime(time: string | null) {
    if (!time) {
      this.closeOverlay();
      return;
    }
    this.selectedTime = time;
    this.confirmed = false;
    this.closeOverlay();
  }

  confirmAppointment(event: MouseEvent) {
    event.stopPropagation();

    if (this.selectedDay && this.selectedTime) {
      this.confirmedAppointment = {
        date: this.selectedDay,
        time: this.selectedTime,
      };
      this.selectedDay = null;
      this.selectedTime = null;
      this.confirmed = true;
      this.closeOverlay();
      this.selectedDate.emit(
        this.toISOStringWithTime(
          this.confirmedAppointment.date,
          this.confirmedAppointment.time,
        ),
      );
    }
  }

  cancelAppointment(event: MouseEvent) {
    event.stopPropagation();
    this.selectedTime = null;
    this.confirmed = false;
    this.closeOverlay();
  }

  clearCreatedAppointment(event: MouseEvent) {
    event.stopPropagation();
    this.confirmed = false;
    this.confirmedAppointment = null;
    this.selectedDay = null;
    this.selectedTime = null;
    this.selectedDate.emit(null);
  }

  previousMonth() {
    this.viewDate = new Date(
      this.viewDate.getFullYear(),
      this.viewDate.getMonth() - 1,
      1,
    );
  }
  nextMonth() {
    this.viewDate = new Date(
      this.viewDate.getFullYear(),
      this.viewDate.getMonth() + 1,
      1,
    );
  }
  goToToday() {
    this.viewDate = new Date();
  }

  getFormattedMonthYear(date: Date): string {
    const month = date.toLocaleDateString('pt-BR', { month: 'long' });
    return `${
      month.charAt(0).toUpperCase() + month.slice(1)
    } de ${date.getFullYear()}`;
  }

  isSameDay(date1: Date, date2: Date | null | undefined): boolean {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return (
      d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear()
    );
  }

  private parseLocalDate(dateString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private toISOStringWithTime(date: Date, time: string): string {
    const [hours, minutes] = time.split(':').map(Number);

    const combined = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      hours,
      minutes,
      0,
      0,
    );

    return combined.toISOString();
  }

  ngOnDestroy() {
    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
    }
  }
}
